import React from 'react';
import {
  ClipboardCheck, ChevronLeft, ChevronRight, Check, X, RotateCcw,
  AlertTriangle, ShieldAlert, UtensilsCrossed, CalendarOff, Users, Loader2
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import LoadingSpinner from '../LoadingSpinner';
import { formatNumber, formatDateForDisplay } from './cantineUtils';

const RegimeBadge = ({ regime }) => (
  regime === 'forfait'
    ? <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Forfait</span>
    : <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Occasionnel</span>
);

const CantinePointageSection = ({
  isLoading,
  isSaving,
  selectedDate,
  onPreviousDay,
  onNextDay,
  onToday,
  onDateChange,
  feuille,
  sections,
  classes,
  filterSection,
  onSectionChange,
  filterClasse,
  setFilterClasse,
  onPointer,
  onAnnulerPointage
}) => {
  const resume = feuille?.resume || {};
  const lignes = feuille?.lignes || [];
  const menu = feuille?.menu || null;
  const estJourDeService = feuille?.est_jour_de_service !== false;

  const classesFiltrees = filterSection
    ? classes.filter((c) => String(c.section_id) === String(filterSection))
    : classes;

  const tauxPointage = resume.attendus > 0
    ? Math.round((resume.pointes / resume.attendus) * 100)
    : 0;

  return (
    <div className="space-y-4">
      {/* Navigation de date + menu du jour */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onPreviousDay}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                title="Jour précédent"
              >
                <ChevronLeft size={18} />
              </button>

              <label className="relative flex items-center px-4 py-2 bg-[#246990] text-white rounded-lg text-sm font-semibold cursor-pointer capitalize min-w-[240px] justify-center">
                {formatDateForDisplay(selectedDate)}
                <input
                  type="date"
                  value={feuille?.date_service || ''}
                  onChange={onDateChange}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>

              <button
                type="button"
                onClick={onNextDay}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                title="Jour suivant"
              >
                <ChevronRight size={18} />
              </button>

              <Button size="sm" variant="outline" onClick={onToday} className="ml-1">
                Aujourd'hui
              </Button>
            </div>

            {menu ? (
              <div className="rounded-lg bg-[#319190]/10 px-4 py-2">
                <p className="text-xs font-semibold uppercase text-[#1f5b5a]">Menu du jour</p>
                <p className="text-sm text-gray-800 mt-0.5">
                  {[menu.entree, menu.plat, menu.dessert].filter(Boolean).join(' · ') || 'Non renseigné'}
                </p>
              </div>
            ) : (
              <div className="rounded-lg bg-gray-100 px-4 py-2">
                <p className="text-xs font-semibold uppercase text-gray-500">Menu du jour</p>
                <p className="text-sm text-gray-500 mt-0.5">Aucun menu saisi</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Jour non servi */}
      {!estJourDeService && (
        <div className="rounded-lg border-2 border-orange-200 bg-orange-50 p-4">
          <div className="flex items-start gap-3">
            <CalendarOff size={20} className="text-orange-600 mt-0.5 flex-shrink-0" />
            <div>
              <h5 className="font-semibold text-orange-900">Jour sans service de cantine</h5>
              <p className="text-sm text-orange-800 mt-1">
                Cette date ne fait pas partie des jours de service configurés. Les abonnés au forfait ne sont pas
                attendus ; seuls les élèves ayant payé un repas ponctuel apparaissent ci-dessous.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cartes de synthèse */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-2 border-blue-200">
          <CardContent className="p-5">
            <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Users size={22} className="text-blue-600" />
            </div>
            <p className="text-gray-600 text-sm mb-3 font-semibold">Attendus au réfectoire</p>
            <div className="space-y-2 bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">Forfait</span>
                <span className="font-semibold text-blue-600">{formatNumber(resume.forfait)}</span>
              </div>
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">Occasionnels payés</span>
                <span className="font-semibold text-purple-600">{formatNumber(resume.occasionnel)}</span>
              </div>
              <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between items-center gap-3">
                <span className="text-gray-700 font-semibold">Couverts à prévoir</span>
                <span className="text-xl font-bold text-blue-700">{formatNumber(resume.attendus)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-2 border-green-200">
          <CardContent className="p-5">
            <div className="w-11 h-11 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Check size={22} className="text-green-600" />
            </div>
            <p className="text-gray-600 text-sm mb-3 font-semibold">Présents</p>
            <div className="space-y-2 bg-white rounded-lg p-3 border border-green-100">
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">Repas servis</span>
                <span className="font-semibold text-green-600">{formatNumber(resume.presents)}</span>
              </div>
              <div className="border-t border-green-200 pt-2 mt-2 flex justify-between items-center gap-3">
                <span className="text-gray-700 font-semibold">Total présents</span>
                <span className="text-xl font-bold text-green-600">{formatNumber(resume.presents)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-orange-50 border-2 border-orange-200">
          <CardContent className="p-5">
            <div className="w-11 h-11 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <X size={22} className="text-orange-600" />
            </div>
            <p className="text-gray-600 text-sm mb-3 font-semibold">Avancement du pointage</p>
            <div className="space-y-2 bg-white rounded-lg p-3 border border-orange-100">
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">Absents</span>
                <span className="font-semibold text-orange-600">{formatNumber(resume.absents)}</span>
              </div>
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">Reste à pointer</span>
                <span className="font-semibold text-gray-700">
                  {formatNumber(Math.max(0, (resume.attendus || 0) - (resume.pointes || 0)))}
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full bg-[#246990]" style={{ width: `${tauxPointage}%` }} />
              </div>
              <div className="border-t border-orange-200 pt-2 mt-2 flex justify-between items-center gap-3">
                <span className="text-gray-700 font-semibold">Pointés</span>
                <span className="text-xl font-bold text-orange-600">{tauxPointage}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-2 border-red-200">
          <CardContent className="p-5">
            <div className="w-11 h-11 bg-red-100 rounded-lg flex items-center justify-center mb-4">
              <ShieldAlert size={22} className="text-red-600" />
            </div>
            <p className="text-gray-600 text-sm mb-3 font-semibold">Allergies critiques</p>
            <div className="space-y-2 bg-white rounded-lg p-3 border border-red-100">
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">Présents aujourd'hui</span>
                <span className="font-semibold text-red-600">Vigilance</span>
              </div>
              <div className="border-t border-red-200 pt-2 mt-2">
                <div className="flex justify-between items-center gap-3">
                  <span className="text-gray-700 font-semibold">Élèves concernés</span>
                  <span className="text-xl font-bold text-red-600">
                    {formatNumber(resume.avec_restriction_critique)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">À signaler à l'équipe avant le service.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feuille de pointage */}
      <Card>
        <CardHeader>
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              <CardTitle className="text-xl text-[#246990] flex items-center gap-2">
                <ClipboardCheck size={24} />
                Feuille de pointage
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                cochez les élèves qui ont effectivement pris leur repas
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Select value={filterSection || 'all'} onValueChange={(v) => onSectionChange(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-44 bg-white border-gray-300">
                  <SelectValue placeholder="Toutes les sections" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les sections</SelectItem>
                  {sections.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.nom}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={filterClasse || 'all'} onValueChange={(v) => setFilterClasse(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-44 bg-white border-gray-300">
                  <SelectValue placeholder="Toutes les classes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les classes</SelectItem>
                  {classesFiltrees.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <LoadingSpinner message="Chargement de la feuille du jour..." color="#246990" />
            </div>
          ) : lignes.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
              <UtensilsCrossed size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 font-medium">Aucun élève attendu ce jour</p>
              <p className="text-gray-500 text-sm mt-2">
                {estJourDeService
                  ? "Vérifiez les inscriptions, ou les paiements des repas ponctuels pour cette date."
                  : "Cette date n'est pas un jour de service de la cantine."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-blue-100 border-b border-blue-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900 uppercase">Élève</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900 uppercase">Classe</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-blue-900 uppercase">Régime</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900 uppercase">Restrictions</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-blue-900 uppercase">Pointage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100">
                  {lignes.map((ligne) => {
                    const critiques = ligne.restrictions.filter((r) => r.severite === 'critique');
                    const autres = ligne.restrictions.filter((r) => r.severite !== 'critique');
                    const estPresent = ligne.statut === 'present';
                    const estAbsent = ligne.statut === 'absent';

                    return (
                      <tr
                        key={ligne.eleve_id}
                        className={`transition-colors ${critiques.length > 0 ? 'bg-red-50/50 hover:bg-red-50' : 'hover:bg-blue-50'}`}
                      >
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">
                            {ligne.prenom} {ligne.nom}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {ligne.matricule || 'Sans matricule'}
                          </p>
                        </td>

                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-900">{ligne.classe_nom || '—'}</p>
                          <p className="text-xs text-gray-500">{ligne.section_nom || ''}</p>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <RegimeBadge regime={ligne.regime_facturation} />
                        </td>

                        <td className="px-4 py-3">
                          {ligne.restrictions.length === 0 ? (
                            <span className="text-sm text-gray-400">—</span>
                          ) : (
                            <div className="space-y-1">
                              {critiques.map((restriction) => (
                                <div
                                  key={restriction.id || restriction.libelle}
                                  className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-100 px-2 py-1 text-xs font-semibold text-red-800 mr-1"
                                >
                                  <AlertTriangle size={12} />
                                  {restriction.libelle}
                                </div>
                              ))}
                              {autres.map((restriction) => (
                                <span
                                  key={restriction.id || restriction.libelle}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-1"
                                >
                                  {restriction.libelle}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                                estPresent
                                  ? 'bg-green-600 text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-700'
                              }`}
                              title="Marquer présent"
                              disabled={isSaving}
                              onClick={() => onPointer(ligne, 'present')}
                            >
                              <Check size={15} />
                              Présent
                            </button>

                            <button
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                                estAbsent
                                  ? 'bg-orange-600 text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-orange-100 hover:text-orange-700'
                              }`}
                              title="Marquer absent"
                              disabled={isSaving}
                              onClick={() => onPointer(ligne, 'absent')}
                            >
                              <X size={15} />
                              Absent
                            </button>

                            {ligne.pointe && (
                              <button
                                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Annuler le pointage"
                                disabled={isSaving}
                                onClick={() => onAnnulerPointage(ligne)}
                              >
                                <RotateCcw size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CantinePointageSection;
